// Lightweight OpenDeploy SSO for the landing page.
//
// The landing page has no login of its own — it reuses the same Casdoor IdP
// and OpenDeploy account as the dashboard, acting as a second OIDC client on
// the `opendeploy.dev` origin. The only thing currently gated behind login is
// event-work upvoting (one OpenDeploy account, one vote).
//
// Flow mirrors dashboard-v2/src/pages/AuthCallback.tsx exactly:
//   1. signIn() → casdoor-js-sdk signin_redirect() → Casdoor login
//   2. Casdoor redirects back to /auth/callback?code&state
//   3. handleAuthCallback(): exchange code → access_token (+ id_token via PKCE),
//      POST the access_token to our backend /v1/auth/token (which provisions
//      the OpenDeploy DB user and returns the merged profile), then persist the
//      id_token. The id_token is the JWT the backend OIDC middleware verifies.
//   4. API calls send `Authorization: Bearer <id_token>`.
//
// NOTE: requires `https://opendeploy.dev/auth/callback` (and the www variant)
// to be registered as redirect URIs on the Casdoor `opendeploy-app` client.
import Sdk from 'casdoor-js-sdk'
import { authApi } from './api'

const STORAGE_KEY = 'od-landing-user'
// Where to return after a sign-in that was triggered to perform an action.
const RETURN_TO_KEY = 'od-auth-return-to'

export interface LandingUser {
  id: string
  username: string
  email: string
  name: string
  avatar: string
  id_token: string
}

const oidcConfig = {
  serverUrl: import.meta.env.VITE_CASDOOR_ENDPOINT || 'https://sso.opendeploy.dev',
  organizationName: import.meta.env.VITE_CASDOOR_ORG || 'opendeploy.dev',
  appName: import.meta.env.VITE_CASDOOR_APP || 'opendeploy-app',
  clientId: import.meta.env.VITE_OIDC_CLIENT_ID || 'bfaf0069e1cde69a07cd',
}

const casdoorSdk = new Sdk({
  serverUrl: oidcConfig.serverUrl,
  clientId: oidcConfig.clientId,
  organizationName: oidcConfig.organizationName,
  appName: oidcConfig.appName,
  redirectPath: '/auth/callback',
})

export function getUser(): LandingUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as LandingUser) : null
  } catch {
    return null
  }
}

export function getToken(): string | null {
  return getUser()?.id_token || null
}

export function isAuthed(): boolean {
  return !!getToken()
}

export function signOut() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event('od-auth-change'))
}

// Kick off SSO. `returnTo` is where /auth/callback sends the user back after a
// successful login (defaults to the current location) so a vote-triggered
// sign-in lands them back on the event page.
export function signIn(returnTo?: string) {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo || window.location.pathname + window.location.search)
  } catch {
    /* ignore */
  }
  casdoorSdk.signin_redirect()
}

export function consumeReturnTo(): string {
  try {
    const v = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
    return v || '/'
  } catch {
    return '/'
  }
}

// Runs on the /auth/callback route. Exchanges the code, provisions the user via
// the backend, and persists the session. Returns the destination to navigate
// to. Throws on failure (caller shows an error and offers retry).
export async function handleAuthCallback(): Promise<string> {
  const params = new URLSearchParams(window.location.search)
  const errorParam = params.get('error')
  if (errorParam) {
    throw new Error(errorParam === 'access_denied' ? 'Sign-in was cancelled.' : `Sign-in failed: ${errorParam}`)
  }
  if (!params.get('code')) {
    throw new Error('Missing authorization code.')
  }

  // PKCE code → token exchange (returns access_token + id_token).
  const tokenResponse: any = await casdoorSdk.exchangeForAccessToken()
  if (!tokenResponse || !tokenResponse.access_token) {
    throw new Error('Unable to obtain access token.')
  }
  const idToken: string =
    tokenResponse.id_token || tokenResponse.idToken || tokenResponse.access_token

  // Provision / fetch the OpenDeploy account (creates the DB user on first
  // sign-in) and read back the canonical profile.
  const data = await authApi.exchangeToken(tokenResponse.access_token)
  const u = (data.user || data) as Record<string, any>

  const user: LandingUser = {
    id: String(u.id || u.sub || ''),
    username: u.username || '',
    email: u.email || '',
    name: u.name || u.username || '',
    avatar: u.avatar || '',
    id_token: idToken,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user))
  window.dispatchEvent(new Event('od-auth-change'))

  return consumeReturnTo()
}
